<template>
  <q-item
    v-bind="{ clickable, disable, dense, active, activeClass, tag, dark, insetLevel, tabindex, manualFocus, focused }"
    class="appointment-item module-item module-item-wrap"
    style="overflow: hidden;"
    @click="$emit('click')"
  >
    <div
      v-if="!withStatus"
      class="appointment-status-line"
      :style="{ backgroundColor: fieldColor }"
    />
    <q-item-section>
      <div class="flex justify-between q-gutter-sm">
        <div
          v-if="withColor"
          style="flex: 0 0 20px"
          class="self-center"
        >
          <span
            class="dot inline-block"
            :style="{'background-color': appointment.color}"
          />
        </div>
        <div style="flex: 1 0 150px;">
          <q-item-label color="blue-9" :lines="2">
            <q-item-label>
              <span class="module-name" :title="appointment.title">{{ appointment.title }}</span>
              <template v-if="appointment.types">
                <q-chip
                  v-for="(type, key) in appointment.types"
                  :key="key"
                  :title="type.name"
                  dense
                  square
                  color="grey-3"
                  size="10px"
                  class="q-my-none q-mx-xs"
                >
                  {{ truncateString(type.shorthand_name || type.name, 35) }}
                </q-chip>
              </template>
            </q-item-label>
            <q-item-label caption>
              ({{ truncateString(appointment.reference_id, 20) }})
            </q-item-label>
          </q-item-label>
          <q-item-label
            v-if="appointment.address"
            caption
            :lines="2"
          >
            {{ appointment.address.full_address }}
          </q-item-label>
        </div>
        <div
          v-if="hasMeta"
          class="row items-start no-wrap q-gutter-x-md"
          style="flex: 0 0 auto;"
        >
          <div
            v-if="withStatus || (!dense && hasValidDates)"
            class="column items-end text-right q-gutter-y-xs"
          >
            <q-chip
              v-if="withStatus"
              dense
              :square="squareStatus"
              size="11px"
              text-color="white"
              :style="{ backgroundColor: fieldColor }"
              class="q-my-none q-mx-none"
            >
              {{ statusLabel }}
            </q-chip>
            <q-item-label
              v-if="!dense && hasValidDates"
              caption
            >
              <DateTimePartial
                :is-scheduled="appointment.status !== 'UNSCHEDULED'"
                :all-day="appointment.all_day"
                :start-date="appointment.start_date || ''"
                :end-date="appointment.end_date || ''"
                :timezone="appointment.timezone_details"
                :short="short"
                without-length
              />
            </q-item-label>
          </div>
          <div
            v-if="showChecklistProgress"
            style="width: 90px;"
          >
            <q-item-label
              caption
              class="text-center q-mb-xs"
            >
              {{ checklistCompleted }}/{{ checklistTotal }}
            </q-item-label>
            <q-linear-progress
              :value="checklistCompleted / checklistTotal"
              :color="checklistCompleted === checklistTotal ? 'green' : 'orange'"
              track-color="blue-grey-3"
              rounded
              size="8px"
            />
          </div>
        </div>
      </div>
    </q-item-section>

    <q-item-section
      v-if="hasDefaultSlot"
      side
      @click="onSideClick"
    >
      <slot />
    </q-item-section>
    <slot name="outer" />
  </q-item>
</template>
<script>
/*
  Partial to display appointment information on any page
*/
import DateTimePartial from './DateTime'
import statusMixin from './mixins/statusMixin'
import itemMixin from 'src/helpers/item'
export default {
  components: {
    DateTimePartial
  },
  mixins: [itemMixin, statusMixin],
  props: {
    appointment: {
      required: true,
      type: Object
    },
    withColor: {
      type: Boolean,
      default: false
    },
    preventSideClick: {
      type: Boolean,
      default: false
    },
    withChecklist: {
      type: Boolean,
      default: false
    },
    withStatus: {
      type: Boolean,
      default: false
    },
    short: {
      type: Boolean,
      default: false
    },
    // Hard-edged status chip to match square row/list corners; defaults to a
    // rounded (soft) chip so it sits well inside rounded-corner lists.
    squareStatus: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    fieldColor () {
      return this.baseFieldColor(this.appointment.status)
    },
    statusLabel () {
      const settings = this.baseSystemStatuses || {}
      return (settings[this.appointment.status] && settings[this.appointment.status].name) || this.appointment.status
    },
    hasDefaultSlot () {
      return !!this.$slots.default
    },
    hasValidDates () {
      return this.appointment.start_date || this.appointment.end_date
    },
    hasMeta () {
      return this.withStatus || (!this.dense && this.hasValidDates) || this.showChecklistProgress
    },
    showChecklistProgress () {
      return this.withChecklist && this.checklistTotal > 0
    },
    checklistTotal () {
      if (!this.appointment.checklists) return 0
      let total = 0
      this.appointment.checklists.forEach(c => {
        if (c.items) total += c.items.length
      })
      return total
    },
    checklistCompleted () {
      if (!this.appointment.checklists) return 0
      let completed = 0
      this.appointment.checklists.forEach(c => {
        if (c.items) completed += c.items.filter(i => i.is_completed).length
      })
      return completed
    }
  },
  methods: {
    onSideClick (e) {
      if (this.preventSideClick) {
        e.stopPropagation()
      }
    }
  }
}
</script>

<style scoped>
/* Full-height status colour bar down the right edge of non-calendar appointment
   rows (clipped to the row's rounded corners via the item's overflow: hidden). */
.appointment-status-line {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 6px;
  pointer-events: none;
}
</style>
